const ALLOWED_TYPE_MAP = {
  ".jpg": "image/jpeg",
  ".jpeg": "image/jpeg",
  ".png": "image/png",
  ".gif": "image/gif",
  ".webp": "image/webp",
  ".pdf": "application/pdf",
  ".txt": "text/plain",
  ".csv": "text/csv",
  ".md": "text/markdown",
  ".markdown": "text/markdown",
  ".py": "text/x-python",
  ".js": "text/javascript",
  ".jsx": "text/javascript",
  ".ts": "text/typescript",
  ".tsx": "text/typescript",
  ".html": "text/html",
  ".htm": "text/html",
  ".css": "text/css",
  ".json": "text/plain",
  ".xml": "text/xml",
  ".yaml": "text/yaml",
  ".yml": "text/yaml",
  ".sql": "text/x-sql",
  ".sh": "text/x-shellscript",
  ".ino": "text/x-arduino",
  ".cpp": "text/x-c++src",
  ".cc": "text/x-c++src",
  ".cxx": "text/x-c++src",
  ".c": "text/x-csrc",
  ".h": "text/x-chdr",
  ".hpp": "text/x-c++hdr",
  ".swift": "text/x-swift",
  ".java": "text/x-java-source",
  ".go": "text/x-go",
  ".rs": "text/x-rustsrc",
};

const MIME_EXTENSION_MAP = Object.fromEntries(
  Object.entries(ALLOWED_TYPE_MAP).map(([ext, mime]) => [mime, ext])
);

export const CHAT_ATTACHMENT_ALLOWED_TYPES = new Set(Object.values(ALLOWED_TYPE_MAP));
export const CHAT_ATTACHMENT_MAX_FILES = 10;
export const CHAT_ATTACHMENT_MAX_SIZE = 10 * 1024 * 1024;

export function getAttachmentContentType(file) {
  if (file?.type && CHAT_ATTACHMENT_ALLOWED_TYPES.has(file.type)) {
    return file.type;
  }
  const name = (file?.name || "").toLowerCase();
  const ext = Object.keys(ALLOWED_TYPE_MAP).find((suffix) => name.endsWith(suffix));
  return ext ? ALLOWED_TYPE_MAP[ext] : (file?.type || "");
}

function getAttachmentExtension(contentType) {
  return MIME_EXTENSION_MAP[contentType] || "";
}

export function normalizeChatAttachment(file, index = 0) {
  const contentType = getAttachmentContentType(file) || "application/octet-stream";
  const baseName = (file?.name || "").trim();
  const filename = baseName || `pasted-file-${Date.now()}-${index}${getAttachmentExtension(contentType)}`;
  if (baseName && file.type === contentType) return file;
  return new File([file], filename, {
    type: contentType,
    lastModified: file?.lastModified || Date.now(),
  });
}

export function isAllowedChatAttachment(file) {
  const contentType = getAttachmentContentType(file);
  if (!contentType || !CHAT_ATTACHMENT_ALLOWED_TYPES.has(contentType)) return false;
  if ((file?.size || 0) > CHAT_ATTACHMENT_MAX_SIZE) return false;
  return true;
}

export function mergeChatAttachments(previousFiles, incomingFiles) {
  const seen = new Set();
  const merged = [];
  for (const [index, sourceFile] of [...previousFiles, ...incomingFiles].entries()) {
    const file = normalizeChatAttachment(sourceFile, index);
    const key = [file.name, file.size, file.lastModified, getAttachmentContentType(file)].join(":");
    if (seen.has(key)) continue;
    seen.add(key);
    if (isAllowedChatAttachment(file)) merged.push(file);
    if (merged.length >= CHAT_ATTACHMENT_MAX_FILES) break;
  }
  return merged;
}

export function extractClipboardFiles(clipboardData) {
  if (!clipboardData) return [];
  const files = [];
  const seen = new Set();

  for (const file of Array.from(clipboardData.files || [])) {
    const key = [file.name, file.size, file.lastModified].join(":");
    if (seen.has(key)) continue;
    seen.add(key);
    files.push(normalizeChatAttachment(file, files.length));
  }

  for (const item of Array.from(clipboardData.items || [])) {
    if (item.kind !== "file") continue;
    const file = item.getAsFile();
    if (!file) continue;
    const key = [file.name, file.size, file.lastModified].join(":");
    if (seen.has(key)) continue;
    seen.add(key);
    files.push(normalizeChatAttachment(file, files.length));
  }

  return files;
}

export function clipboardHasInlineText(clipboardData) {
  if (!clipboardData) return false;
  return Array.from(clipboardData.items || []).some(
    (item) => item.kind === "string" && (item.type === "text/plain" || item.type === "text/html")
  );
}

export async function readClipboardFiles() {
  if (typeof navigator === "undefined" || !navigator.clipboard?.read) return [];

  try {
    const items = await navigator.clipboard.read();
    const files = [];

    for (const item of items) {
      for (const type of item.types) {
        if (!CHAT_ATTACHMENT_ALLOWED_TYPES.has(type)) continue;
        if (type.startsWith("text/")) continue;
        const blob = await item.getType(type);
        files.push(normalizeChatAttachment(blob, files.length));
      }
    }

    return files;
  } catch {
    return [];
  }
}

export async function uploadChatAttachments(files) {
  if (!files?.length) return [];
  const normalizedFiles = files.map((file, index) => normalizeChatAttachment(file, index));

  const API_BASE = import.meta.env.VITE_API_URL || "/api";
  const token = localStorage.getItem("anna_token");
  const res = await fetch(`${API_BASE}/uploads`, {
    method: "POST",
    headers: {
      "Content-Type": "application/json",
      ...(token ? { Authorization: `Bearer ${token}` } : {}),
    },
    body: JSON.stringify({
      files: normalizedFiles.map((file) => ({
        filename: file.name,
        contentType: getAttachmentContentType(file) || "application/octet-stream",
        size: file.size,
      })),
    }),
  });

  if (!res.ok) {
    const err = await res.json().catch(() => ({}));
    throw new Error(err.error || `Upload failed: ${res.status}`);
  }

  const { uploads } = await res.json();

  await Promise.all(
    uploads.map((upload, index) =>
      fetch(upload.uploadUrl, {
        method: "PUT",
        headers: {
          "Content-Type": upload.contentType || getAttachmentContentType(normalizedFiles[index]) || "application/octet-stream",
        },
        body: normalizedFiles[index],
      }).then((putRes) => {
        if (!putRes.ok) throw new Error(`Upload failed: ${putRes.status}`);
      })
    )
  );

  return uploads.map((upload) => ({
    filename: upload.filename,
    contentType: upload.contentType,
    size: upload.size,
    s3Key: upload.s3Key,
  }));
}